import { useEffect, useRef, useState } from 'react';
import {
  Routes,
  Route,
  useLocation,
  Navigate,
} from 'react-router-dom';
import Survey from './components/survey_page';
import Verification from './components/verification';
import StaffVerification from './components/staff_verification';
import HealthCheck from './components/health-check';
import LandingPage from './components/landing_page';
import PageNotFound from 'components/404';
import { Layout } from 'antd';
import { checkScreenSize } from './utilities/breakpoints';
import { COLORS } from './themes/colors';
import { ConfigProvider as AntdProvider } from 'antd';
import { IdleTimer } from 'components/modals';
import { getTheme } from './themes/themes';
import {
  careProviderConfiguration,
  verificationTokenAtom,
  interactionIdAtom,
  enableResumingSurveysAtom,
  languagesAtom,
  selectedLanguageAtom,
  decodedTokenAtom,
} from 'store';
import { useAtom } from 'jotai';
import { useGetQuery } from 'api/hooks/useGetQuery';
import Bugsnag from '@bugsnag/js';
import { useDeactivateSession } from 'api/hooks/useDeactivateSession';
import type { LanguageOption } from 'store';
import { getUserTypeFromToken } from 'utilities/helpers';

const { Content } = Layout;
const TIMEOUT_IN_SECONDS = 480;
const SHOW_TIMEOUT_PROMPT_IN_SECONDS = 120;

interface ProviderConfig {
  openingMessage?: string;
  welcomeMessage?: string;
  homeScreenMessage?: string;
  staffOpeningMessage?: string;
  staffWelcomeMessage?: string;
  staffHomeScreenMessage?: string;
  staffAnonymousModeEnabled?: boolean;
  enableResumingSurveys: boolean;
  theme: {
    logo: string;
    botAvatar: string;
    botName: string;
    colorPrimary: string;
    colorBgHeader: string;
    avatarFillColor: string;
    landingPageBackgroundColor: string;
  };
  languages?: LanguageOption[];
  staffLanguages?: LanguageOption[];
}

// Query parameter used to indicate staff mode pre-login (e.g. /central?member=staff)
const MEMBER_TYPE_QUERY_PARAM = 'member';
const STAFF_MEMBER_TYPE = 'staff';

// Helper to determine user type from URL (for pre-login)
const getUserTypeFromUrl = (location: { search: string }): 'patient' | 'staff' => {
  const params = new URLSearchParams(location.search);
  if (params.get(MEMBER_TYPE_QUERY_PARAM) === STAFF_MEMBER_TYPE) {
    return 'staff';
  }
  return 'patient';
};

function App() {
  const screenSize = checkScreenSize();
  const [token] = useAtom(verificationTokenAtom);
  const tokenRef = useRef(token);
  tokenRef.current = token;
  const [interactionId] = useAtom(interactionIdAtom);
  const [, setEnableResumingSurveys] = useAtom(enableResumingSurveysAtom);
  // the app should be unauthenticated by default
  const [isUnauthenticatedPath, setIsUnauthenticationPath] = useState(true);
  const location = useLocation();
  const pathnameItems = location.pathname.split('/');
  const careProviderSlug = pathnameItems[1];
  const deactivate = useDeactivateSession();

  const [decodedToken] = useAtom(decodedTokenAtom);

  const { data, exec, isPending, isIdle, isSuccess, isError }
    = useGetQuery<ProviderConfig>(`provider_config/${careProviderSlug}`);

  const [, setCareProviderStyles] = useAtom(careProviderConfiguration);
  const [, setLanguages] = useAtom(languagesAtom);
  const [selectedLanguage, setSelectedLanguage] = useAtom(selectedLanguageAtom);

  // For pre-login: derive from URL
  // For post-login: derive from decoded token
  const preLoginUserType = getUserTypeFromUrl(location);
  const postLoginUserType = getUserTypeFromToken(decodedToken);
  const userType = token && decodedToken ? postLoginUserType : preLoginUserType;

  const config = data
    ? userType === 'staff'
      ? {
          openingMessage: data.staffOpeningMessage,
          welcomeMessage: data.staffWelcomeMessage,
          homeScreenMessage: data.staffHomeScreenMessage,
          languages: data.staffLanguages,
        }
      : {
          openingMessage: data.openingMessage,
          welcomeMessage: data.welcomeMessage,
          homeScreenMessage: data.homeScreenMessage,
          languages: data.languages,
        }
    : null;

  // Validate token matches current provider
  useEffect(() => {
    if (token && decodedToken) {
      if (decodedToken.careProviderSlug !== careProviderSlug) {
        deactivate('/404');
      }
    }
    else if (token && !decodedToken) {
      // Token exists but failed to decode (decodedTokenAtom returned null)
      Bugsnag.notify(new Error('Invalid token'), (report) => {
        report.addMetadata('request', { token });
      });
      deactivate('/404');
    }
  }, [token, decodedToken, careProviderSlug]);

  useEffect(() => {
    if (!location.pathname.includes('404')) exec();
  }, []);

  useEffect(() => {
    if (pathnameItems.length <= 2 || location.pathname.includes('404')) {
      setIsUnauthenticationPath(true);
    }
    else setIsUnauthenticationPath(false);
  }, [location.pathname]);

  // Clear session data when landing on verification page.
  // tokenRef is used (instead of token) so that changes to the token during
  // an active login flow do not trigger this effect — only path changes should.
  useEffect(() => {
    const isVerificationPage = pathnameItems.length === 2 && !location.pathname.includes('404');
    if (isVerificationPage && tokenRef.current) {
      // User has a stale token but landed on the verification page - clear the session
      const currentPath = location.pathname + location.search;
      deactivate(currentPath);
    }
  }, [location.pathname, location.search, deactivate]);

  useEffect(() => {
    if (isSuccess && data) {
      setEnableResumingSurveys(data.enableResumingSurveys);
      setCareProviderStyles({
        logo: data.theme?.logo || 'cipherLogo',
        botAvatar: data.theme?.botAvatar || 'cipherAvatar',
        botName: data.theme?.botName || '',
        colorPrimary: data.theme?.colorPrimary || COLORS.primary,
        avatarFillColor:
          data.theme?.avatarFillColor
          || data.theme?.colorPrimary
          || COLORS.cipherGreenL0,
        colorBgHeader: data.theme?.colorBgHeader || '',
        landingPageBackgroundColor:
          data?.theme?.landingPageBackgroundColor || '#e0fff6',
      });

      const languageSource = config?.languages;
      if (Array.isArray(languageSource)) {
        setLanguages(languageSource);
        if (!selectedLanguage) {
          setSelectedLanguage(languageSource[0]?.code || 'en');
        }
      }
    }
    else if (isError) {
      deactivate('/404');
    }
  }, [isSuccess, isError, userType]);

  return (
    <AntdProvider theme={getTheme()}>
      {!isUnauthenticatedPath && (
        <IdleTimer
          timeOutInSeconds={TIMEOUT_IN_SECONDS}
          showTimeOutPromptInSeconds={SHOW_TIMEOUT_PROMPT_IN_SECONDS}
          interactionId={interactionId}
        />
      )}
      <Layout
        style={{
          padding: screenSize === 'desktop' ? '50px 100px 0' : '0',
          background: COLORS.lightGray,
          alignItems: 'center',
        }}
      >
        <Content
          style={{
            background: COLORS.whiteL0,
            maxWidth: '1024px',
            width: '100%',
            borderRadius: '4px',
            border: `1px solid ${COLORS.tundoraL2}`,
            borderBottom: 'none',
          }}
        >
          <Routes>
            <Route
              path="/:careProviderSlug"
              index
              element={
                userType === 'staff'
                  ? (
                      <StaffVerification
                        configLoading={isPending}
                        configIdling={isIdle}
                        staffAnonymousModeEnabled={data?.staffAnonymousModeEnabled}
                        displayText={{
                          openingMessage: config?.openingMessage,
                          welcomeMessage: config?.welcomeMessage,
                        }}
                      />
                    )
                  : (
                      <Verification
                        configLoading={isPending}
                        configIdling={isIdle}
                        displayText={{
                          openingMessage: config?.openingMessage,
                          welcomeMessage: config?.welcomeMessage,
                        }}
                      />
                    )
              }
            />
            <Route
              path="/:careProviderSlug?/landing-page"
              element={
                token
                  ? (
                      <LandingPage
                        homeScreenMessage={config?.homeScreenMessage}
                      />
                    )
                  : (
                      <Navigate to={`/${careProviderSlug}`} />
                    )
              }
            />
            <Route
              path="/:careProviderSlug?/survey/:scriptId"
              element={
                token ? <Survey /> : <Navigate to={`/${careProviderSlug}`} />
              }
            />
            <Route path="/health-check" element={<HealthCheck />} />
            <Route path="/404" element={<PageNotFound />} />
            <Route path="*" element={<PageNotFound />} />
          </Routes>
        </Content>
      </Layout>
    </AntdProvider>
  );
}

export default App;
